import React, { useState } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Copy, Sparkles, Check } from 'lucide-react'
import { APIRequest, APIResponse } from '../../stores/apiStore'
import { APIService } from '../../services/apiService'
import { useToast } from '../ui/ToastProvider'

interface AIPromptModalProps {
  isOpen: boolean
  onClose: () => void
  request: Partial<APIRequest>
  response: APIResponse
}

const languages = [
  { id: 'javascript', name: 'JavaScript (Axios)' },
  { id: 'python', name: 'Python (requests)' },
  { id: 'php', name: 'PHP (cURL)' },
  { id: 'nodejs', name: 'Node.js' },
  { id: 'flutter', name: 'Flutter (Dart)' },
  { id: 'kotlin', name: 'Kotlin (Android)' },
  { id: 'swift', name: 'Swift (iOS)' },
  { id: 'react-native', name: 'React Native' }
]

const AIPromptModal: React.FC<AIPromptModalProps> = ({ isOpen, onClose, request, response }) => {
  const [selectedLanguage, setSelectedLanguage] = useState('javascript')
  const [promptStyle, setPromptStyle] = useState<'structured' | 'conversational'>('structured')
  const [copied, setCopied] = useState(false)
  const { showSuccess, showError } = useToast()
  const apiService = APIService.getInstance()

  const prompt = apiService.generateAIPrompt(request, response, selectedLanguage, promptStyle)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(prompt)
      setCopied(true)
      showSuccess('Prompt copied to clipboard!')
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      showError('Failed to copy prompt')
    }
  }

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-[100]"
            onClick={onClose}
          />

          {/* Modal Container to ensure centering */}
          <div className="fixed inset-0 z-[101] flex items-center justify-center p-4 pointer-events-none">
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="w-full max-w-4xl max-h-[90vh] bg-white dark:bg-gray-800 rounded-lg shadow-2xl flex flex-col pointer-events-auto"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-2">
                  <Sparkles className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Generate AI Prompt
                  </h2>
                </div>
                <button
                  onClick={onClose}
                  className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-auto p-4 space-y-4">
                {/* Language Selector */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Platform / Language
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {languages.map((lang) => (
                      <button
                        key={lang.id}
                        onClick={() => setSelectedLanguage(lang.id)}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                          selectedLanguage === lang.id
                            ? 'bg-primary-600 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        {lang.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Prompt Style Tabs */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Prompt Style
                  </label>
                  <div className="flex bg-gray-100 dark:bg-gray-700 rounded-md p-1">
                    <button
                      onClick={() => setPromptStyle('structured')}
                      className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        promptStyle === 'structured'
                          ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                      }`}
                    >
                      Structured
                    </button>
                    <button
                      onClick={() => setPromptStyle('conversational')}
                      className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        promptStyle === 'conversational'
                          ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                      }`}
                    >
                      Conversational
                    </button>
                  </div>
                </div>

                {/* Prompt Content */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Generated Prompt
                    </label>
                    <button
                      onClick={handleCopy}
                      className={`flex items-center space-x-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                        copied
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                          : 'bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-300 hover:bg-primary-200 dark:hover:bg-primary-800'
                      }`}
                    >
                      {copied ? (
                        <>
                          <Check className="w-4 h-4" />
                          <span>Copied!</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          <span>Copy to Clipboard</span>
                        </>
                      )}
                    </button>
                  </div>
                  <div className="relative">
                    <pre className="w-full h-96 p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md overflow-auto text-sm font-mono text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                      {prompt}
                    </pre>
                  </div>
                </div>

                {/* Instructions */}
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
                  <p className="text-sm text-blue-800 dark:text-blue-300">
                    <strong>Tip:</strong> Copy this prompt and paste it into AI coding assistants like Cursor, Windsurf, or ChatGPT to get help integrating this API endpoint into your {languages.find(l => l.id === selectedLanguage)?.name} application.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  )

  return createPortal(modalContent, document.body)
}

export default AIPromptModal
